import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Box, Text, Spinner, useColorModeValue } from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { useAnalytics } from '../../../contexts/AnalyticsContext';

const MoodTrendsChart: React.FC = () => {
  const { moodTrends, isLoading, error } = useAnalytics();
  
  // Define all color mode values at the top
  const cartesianGridColor = useColorModeValue('#E2E8F0', '#2D3748');
  const axisTickColor = useColorModeValue('#1A202C', '#E2E8F0');
  const tooltipBackgroundColor = useColorModeValue('#FFFFFF', '#1A202C');
  const tooltipLabelColor = useColorModeValue('#1A202C', '#E2E8F0');

  const lineColors = {
    'Amazing': '#ECC94B', // yellow.400
    'Happy': '#48BB78',   // green.400
    'Good': '#4299E1',    // blue.400
    'Okay': '#9F7AEA',    // purple.400
    'Down': '#F56565',    // red.400
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  if (!moodTrends.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <Text>No mood data available for the selected period</Text>
      </Box>
    );
  }

  const data = moodTrends.map(trend => ({
    ...trend,
    date: format(parseISO(trend.date), 'MMM d'),
  }));

  return (
    <Box h="400px">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={cartesianGridColor} />
          <XAxis
            dataKey="date"
            tick={{ fill: axisTickColor }}
          />
          <YAxis
            tick={{ fill: axisTickColor }}
            label={{
              value: 'Number of Entries',
              angle: -90,
              position: 'insideLeft',
              fill: axisTickColor,
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: tooltipBackgroundColor,
              border: '1px solid #CBD5E0',
            }}
            labelStyle={{ color: tooltipLabelColor }}
          />
          <Legend />
          {Object.entries(lineColors).map(([label, color]) => (
            <Line
              key={label}
              type="monotone"
              dataKey={`mood_labels.${label}`}
              stroke={color}
              name={label}
              strokeWidth={2}
              dot={{ fill: color }}
              activeDot={{ r: 8 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default MoodTrendsChart; 